// React Data Grid Component
import {
	AllCommunityModule,
	ColDef,
	ColGroupDef,
	ColTypeDef,
	FirstDataRenderedEvent,
	GridApi,
	GridReadyEvent,
	GridSizeChangedEvent,
	ISizeColumnsToFitParams,
	ModuleRegistry,
} from "ag-grid-community";
import { AgGridReact, AgGridReactProps } from "ag-grid-react";
import React, { forwardRef, useCallback, useEffect, useMemo, useRef } from "react";

import { faMagnifyingGlass } from "@performyard/fontawesome/pro-regular-svg-icons";

import { PYBaseTableTheme } from "./BaseTableStyle";

import { Icon, Text } from "../../atoms";
import { Skeleton } from "../../atoms/Skeleton";
import DateCol from "./StandardColumns/DateCol";
import DropdownOptionCol from "./StandardColumns/DropdownOptionCol";
import EmployeeCol from "./StandardColumns/EmployeeCol";
import LinkCol from "./StandardColumns/LinkCol";
import ProgressCol from "./StandardColumns/ProgressCol";
import TextCol from "./StandardColumns/TextCol";
import "./Table.css";

// Register all Community features
ModuleRegistry.registerModules([AllCommunityModule]);

export type TableProps<TData> = Omit<AgGridReactProps<TData>, "columnDefs"> & {
	/**
	 * If true, the table will use autoHeight layout (table hugs content)
	 * If false or undefined, the table will use container height (scrollable)
	 */
	autoHeight?: boolean;
	/**
	 * Controls how columns are sized horizontally.
	 * - "fixed": AG Grid uses column definitions as provided.
	 * - "fit-grid": columns are scaled to fill the rendered table width; compact/fixed columns remain fixed.
	 */
	columnSizing?: "fixed" | "fit-grid";
	columnDefs?: PYColumnDef<TData>[];
	emptyStateText?: string;
	fitGridColumnSizing?: ISizeColumnsToFitParams;
	/** Minimum height of the table wrapper (e.g. 356 or "356px"). Useful with autoHeight when few rows. */
	minHeight?: number | string;
};
export type PYColumnDefCol<TData> = Omit<ColDef<TData>, "field"> & {
	type?: "text" | "employee" | "date" | "link" | "dropdown" | "progress" | string | string[];
	field?: string;
};
export type PYColumnDef<TData> = PYColumnDefCol<TData> | ColGroupDef<TData>;

export type TableRef<TData> = AgGridReact<TData>;

const defaultColumnTypes: { [key: string]: ColTypeDef } = {
	text: TextCol,
	employee: EmployeeCol,
	date: DateCol,
	link: LinkCol,
	dropdown: DropdownOptionCol,
	progress: ProgressCol,
};

export const FIT_GRID_FIXED_WIDTH_THRESHOLD = 80;
const DEFAULT_FIT_GRID_COLUMN_MIN_WIDTH = 56;

const isColumnGroup = <TData,>(col: PYColumnDef<TData>): col is ColGroupDef<TData> => {
	return "children" in col;
};

const isFixedWidthColumn = <TData,>(col: PYColumnDefCol<TData>) => {
	if (col.suppressSizeToFit) return true;
	if (
		typeof col.width === "number" &&
		typeof col.minWidth === "number" &&
		typeof col.maxWidth === "number" &&
		col.width === col.minWidth &&
		col.width === col.maxWidth
	) {
		return true;
	}
	return typeof col.width === "number" && col.width <= FIT_GRID_FIXED_WIDTH_THRESHOLD;
};

export const applyFitGridColumnSizing = <TData,>(
	columnDefs: PYColumnDef<TData>[] | undefined
): PYColumnDef<TData>[] | undefined => {
	if (!columnDefs) return columnDefs;
	return columnDefs.map((col) => {
		if (isColumnGroup(col)) {
			return {
				...col,
				children: applyFitGridColumnSizing(col.children as PYColumnDef<TData>[]) as (
					| ColDef<TData>
					| ColGroupDef<TData>
				)[],
			};
		}
		if (isFixedWidthColumn(col)) {
			return { ...col, suppressSizeToFit: true };
		}
		return col;
	});
};

//set default column with sorting, filtering, resizing, and moveable to false
const defaultColumnDefs: ColDef = {
	sortable: false,
	filter: false,
	resizable: false,
	suppressMovable: true,
	cellRendererSelector: (params) => {
		// If data is empty object (loading state), show shimmer
		if (params.data && Object.keys(params.data).length === 0) {
			return { component: ShimmerCellRenderer };
		}
		return undefined;
	},
};
const TableComponent = <TData,>(props: TableProps<TData>, ref: React.ForwardedRef<AgGridReact<TData>>) => {
	const {
		autoHeight,
		columnSizing = "fixed",
		emptyStateText = "No results found",
		fitGridColumnSizing,
		getRowId,
		minHeight,
		onFirstDataRendered,
		onGridReady,
		onGridSizeChanged,
		theme,
		...otherProps
	} = props;
	const tableWrapperRef = useRef<HTMLDivElement | null>(null);
	const gridApiRef = useRef<GridApi<TData> | null>(null);
	const gridRef = useRef<AgGridReact<TData> | null>(null);
	const resizeAnimationFrameRef = useRef<number | undefined>(undefined);
	const resizeTimeoutRef = useRef<number | undefined>(undefined);
	const usesFitGridSizing = columnSizing === "fit-grid";

	const columnTypes = useMemo(
		() => ({
			...defaultColumnTypes,
			...props?.columnTypes,
		}),
		[props?.columnTypes]
	);

	const columnDefs = useMemo(
		() => (usesFitGridSizing ? applyFitGridColumnSizing(props.columnDefs) : props.columnDefs),
		[props.columnDefs, usesFitGridSizing]
	);

	const rowData = useMemo(() => {
		if (props.loading) {
			return Array.from({ length: 20 }, () => ({}) as TData);
		}
		return props.rowData;
	}, [props.loading, props.rowData]);

	const hasNoRowData = useMemo(() => {
		return !props.loading && (!rowData || rowData.length === 0);
	}, [rowData, props?.loading]);

	const minHeightCss =
		minHeight === undefined ? undefined : typeof minHeight === "number" ? `${minHeight}px` : minHeight;

	const sizeColumnsToFitParams = useMemo(
		() => ({
			defaultMinWidth: DEFAULT_FIT_GRID_COLUMN_MIN_WIDTH,
			...fitGridColumnSizing,
		}),
		[fitGridColumnSizing]
	);

	const sizeColumnsToFit = useCallback(() => {
		const api = gridApiRef.current;
		const tableWidth = tableWrapperRef.current?.getBoundingClientRect().width ?? 0;
		if (!usesFitGridSizing || !api || tableWidth <= 0) return;
		api.sizeColumnsToFit(sizeColumnsToFitParams);
	}, [sizeColumnsToFitParams, usesFitGridSizing]);

	const scheduleSizeColumnsToFit = useCallback(
		({ includePostPaintPass = false }: { includePostPaintPass?: boolean } = {}) => {
			if (!usesFitGridSizing) return;
			if (resizeAnimationFrameRef.current !== undefined) {
				window.cancelAnimationFrame(resizeAnimationFrameRef.current);
			}
			resizeAnimationFrameRef.current = window.requestAnimationFrame(() => {
				resizeAnimationFrameRef.current = undefined;
				sizeColumnsToFit();
			});
			if (includePostPaintPass) {
				if (resizeTimeoutRef.current !== undefined) {
					window.clearTimeout(resizeTimeoutRef.current);
				}
				resizeTimeoutRef.current = window.setTimeout(() => {
					resizeTimeoutRef.current = undefined;
					sizeColumnsToFit();
				}, 50);
			}
		},
		[sizeColumnsToFit, usesFitGridSizing]
	);

	const setGridRef = useCallback(
		(node: AgGridReact<TData> | null) => {
			gridRef.current = node;
			if (typeof ref === "function") {
				ref(node);
			} else if (ref) {
				ref.current = node;
			}
		},
		[ref]
	);

	const handleGridReady = useCallback(
		(event: GridReadyEvent<TData>) => {
			gridApiRef.current = event.api;
			onGridReady?.(event);
			scheduleSizeColumnsToFit({ includePostPaintPass: true });
		},
		[onGridReady, scheduleSizeColumnsToFit]
	);

	const handleFirstDataRendered = useCallback(
		(event: FirstDataRenderedEvent<TData>) => {
			onFirstDataRendered?.(event);
			scheduleSizeColumnsToFit({ includePostPaintPass: true });
		},
		[onFirstDataRendered, scheduleSizeColumnsToFit]
	);

	const handleGridSizeChanged = useCallback(
		(event: GridSizeChangedEvent<TData>) => {
			onGridSizeChanged?.(event);
			scheduleSizeColumnsToFit();
		},
		[onGridSizeChanged, scheduleSizeColumnsToFit]
	);

	useEffect(() => {
		if (!usesFitGridSizing) return;
		scheduleSizeColumnsToFit({ includePostPaintPass: true });
	}, [columnDefs, rowData, scheduleSizeColumnsToFit, usesFitGridSizing]);

	useEffect(() => {
		if (!usesFitGridSizing || !tableWrapperRef.current || typeof ResizeObserver === "undefined") return;
		const resizeObserver = new ResizeObserver((entries) => {
			const width = entries[0]?.contentRect.width ?? 0;
			if (width > 0) scheduleSizeColumnsToFit();
		});
		resizeObserver.observe(tableWrapperRef.current);
		return () => resizeObserver.disconnect();
	}, [scheduleSizeColumnsToFit, usesFitGridSizing]);

	useEffect(() => {
		return () => {
			if (resizeAnimationFrameRef.current !== undefined) {
				window.cancelAnimationFrame(resizeAnimationFrameRef.current);
			}
			if (resizeTimeoutRef.current !== undefined) {
				window.clearTimeout(resizeTimeoutRef.current);
			}
		};
	}, []);

	return (
		<div
			ref={tableWrapperRef}
			style={{
				...(minHeightCss !== undefined ? { minHeight: minHeightCss } : {}),
				height: hasNoRowData ? "356px" : autoHeight ? "auto" : "100%",
			}}
			className="py-table cp-ag-grid-table size-full bg-neutral-0"
		>
			<AgGridReact<TData>
				defaultColDef={defaultColumnDefs}
				suppressCellFocus={true}
				getRowId={props.loading ? () => `loading-${Math.random()}` : getRowId}
				noRowsOverlayComponent={() => (
					<div className="flex flex-col items-center justify-center gap-2">
						<div className="rounded-full bg-neutral-2 p-2">
							<Icon icon={faMagnifyingGlass} size={24} />
						</div>
						<Text size="paragraph-1" weight="semi-bold">
							{emptyStateText}
						</Text>
					</div>
				)}
				{...otherProps}
				columnDefs={columnDefs as (ColDef<TData> | ColGroupDef<TData>)[]}
				rowData={rowData}
				theme={theme ?? PYBaseTableTheme}
				columnTypes={columnTypes}
				ref={setGridRef}
				onGridReady={handleGridReady}
				onFirstDataRendered={handleFirstDataRendered}
				onGridSizeChanged={handleGridSizeChanged}
				loadingOverlayComponent={() => <div />}
				// we don't want auto height when there is no row data, so we can control the height of teh empty state table and overlay
				domLayout={autoHeight && !hasNoRowData ? "autoHeight" : undefined}
				suppressRowHoverHighlight={true}
			/>
		</div>
	);
};

export * from "./BaseTableStyle";

// Export AG Grid types for consumers
export type {
	ColDef,
	ColGroupDef,
	ColTypeDef,
	ICellRendererParams,
	ValueFormatterParams,
	ValueGetterParams,
	CellClassParams,
	SortDirection,
	SortChangedEvent,
	IRowNode,
} from "ag-grid-community";
export type { AgGridReactProps } from "ag-grid-react";

export const Table = forwardRef(TableComponent) as <TData>(
	props: TableProps<TData> & { ref?: React.ForwardedRef<AgGridReact<TData>> }
) => React.ReactElement;

const ShimmerCellRenderer = () => {
	return (
		<div className="flex size-full min-h-0 items-center justify-start">
			<Skeleton />
		</div>
	);
};
